import { useState } from 'react';
import { Input } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { CtyButton } from '../button/CtyButton';
import { CtyShowToast, checkType } from '../toast/CtyToast';
import { useTranslation } from 'react-i18next';

export type CopyCodeFieldLayout = 'vertical' | 'horizontal';

const CODE_MIN_LENGTH = 8;
const isCodeValid = (val: string) => new RegExp(`^[a-zA-Z0-9]{${CODE_MIN_LENGTH},}$`).test(val ?? '');

/** Include `code` in a payload only when it has a non-empty trimmed value. */
export function optionalCodePayload(code?: string | null): { code: string } | Record<string, never> {
    const trimmed = code?.trim();
    return trimmed ? { code: trimmed } : {};
}

interface CopyCodeFieldProps {
    label: string;
    value: string;
    className?: string;
    layout?: CopyCodeFieldLayout;
    isReadOnly?: boolean;
    isRequired?: boolean;
    subText?: string;
    onValueChange?: (value: string) => void;
}

export const CopyCodeField = ({ label, value, className, layout = 'vertical', isReadOnly = true, isRequired, subText, onValueChange }: CopyCodeFieldProps) => {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    const [isTouched, setIsTouched] = useState(false);

    const showError = !isReadOnly && isTouched && value.trim().length > 0 && !isCodeValid(value);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            CtyShowToast(checkType.success, t('Copied to clipboard'));
            setTimeout(() => setCopied(false), 2000);
        } catch {
            CtyShowToast(checkType.error, t('Failed to copy'));
        }
    };

    const input = (
        <Input
            value={value}
            isReadOnly={isReadOnly}
            isRequired={isRequired}
            size="sm"
            aria-label={label}
            classNames={{ inputWrapper: 'h-8 min-h-8', innerWrapper: 'items-center' }}
            onValueChange={onValueChange}
            onBlur={isReadOnly ? undefined : () => setIsTouched(true)}
            isInvalid={showError}
            errorMessage={showError ? t('Code should be at least 8 alphanumeric characters') : undefined}
            endContent={
                isReadOnly ? (
                    <CtyButton
                        isIconOnly
                        variant="light"
                        size="sm"
                        onPress={handleCopy}
                        className="min-w-7 h-7 rounded-md data-[hover=true]:bg-default-100"
                    >
                        <Icon
                            icon={copied ? 'mdi:check-circle' : 'hugeicons:copy-01'}
                            height={16}
                            width={16}
                            className={copied ? 'text-success' : 'text-default-500'}
                        />
                    </CtyButton>
                ) : undefined
            }
        />
    );

    const showSubText = !isReadOnly && !!subText;

    if (layout === 'horizontal') {
        return (
            <div className={`flex flex-row gap-3 ${className ?? ''}`}>
                <label className="flex flex-col w-[30%]">
                    <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                        {t(label)}{isRequired && <span className="text-danger">*</span>}
                    </span>
                    {showSubText && (
                        <span className="font-[Inter] font-normal text-sm leading-5 tracking-normal text-[#535862]">
                            {t(subText)}
                        </span>
                    )}
                </label>
                <div className="flex-1">{input}</div>
            </div>
        );
    }

    return (
        <div className={className}>
            <label className="flex items-center text-sm pb-1 font-medium">
                {t(label)}{isRequired && <span className="text-danger">*</span>}
            </label>
            {showSubText && (
                <div className="text-xs text-default-500 mb-[4px]">{t(subText)}</div>
            )}
            {input}
        </div>
    );
};